import { Button, PaginationProps, Popconfirm, Space, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from '../../i18n/i18n';
import useAuthStore from '../../stores/auth.store';
import EmptyState from '../utils/EmptyState/EmptyState';
import EditInstallation from '../installation-location/InstallationLocationDetail/EditInstallation';
import { DeleteOutlined } from '@ant-design/icons';
import './InstallationTable.scss';
import { installationApi } from '../../api/installation.api';

const { Text } = Typography;

export interface InstallationTableItem {
  uuid: string;
  id: string;
  name: string;
  numDevices: number;
  statusColor: string;
  statusName: string;
  percentageSiphon: number;
}

type InstallationTableProps = {
  data: readonly InstallationTableItem[];
  pagination: PaginationProps;
  onRowClick: (record: InstallationTableItem) => void;
  loading?: boolean;
  onInstallationUpdated?: () => void;
  isAdminOfLocation?: boolean;
};

const InstallationTable = (props: InstallationTableProps) => {
  const { t } = useTranslation('installation-location', { keyPrefix: 'installationLocationDetail.table' });
  const { t: tCommon } = useTranslation('common');
  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const canEdit = isSuperAdminOrCompanyAdmin || !!props.isAdminOfLocation;

  const { onInstallationUpdated } = props;
  const deleteInstallation = useCallback(
    async (uuid: string) => {
      try {
        await installationApi.deleteInstallation(uuid);
        onInstallationUpdated?.();
      } catch (error) {
        //Error catched in main page
      }
    },
    [onInstallationUpdated],
  );

  const columns = useMemo<ColumnsType<InstallationTableItem>>(
    () =>
      [
        {
          title: t('id'),
          dataIndex: 'id',
          key: 'id',
          render: (text: string) => <Text className={'MainTableText'}>{text}</Text>,
          width: '18%',
        },
        {
          title: t('name'),
          dataIndex: 'name',
          key: 'name',
          width: '30%',
        },
        {
          title: t('numDevices'),
          dataIndex: 'numDevices',
          width: '15%',
        },
        {
          title: t('state'),
          key: 'state',
          width: '12%',
          render: (_: unknown, record: InstallationTableItem) => (
            <Space>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: record.statusColor,
                }}
              />
              <Text>{record.statusName}</Text>
            </Space>
          ),
        },
        {
          title: t('perSiphon'),
          dataIndex: 'percentageSiphon',
          key: 'percentageSiphon',
          width: '15%',
          render: (value: number) => <Text>{value}%</Text>,
        },
        {
          title: t('action'),
          key: 'action',
          width: '10%',
          render: (_: unknown, record: InstallationTableItem) => (
            <div onClick={(e) => e.stopPropagation()} className={'InstalationLocation__table__action'}>
              <EditInstallation uuid={record.uuid} onSuccess={props.onInstallationUpdated} areaToEdit={'name'} />
              {isSuperAdminOrCompanyAdmin && (
                <Popconfirm
                  title={t('delete')}
                  okText={t('yes')}
                  description={t('deleteDesc')}
                  placement={'left'}
                  onConfirm={() => {
                    deleteInstallation(record.uuid);
                  }}
                >
                  <Button type={'text'}>
                    <DeleteOutlined className={'InstalationLocation__table__action__delete'} />
                  </Button>
                </Popconfirm>
              )}
            </div>
          ),
        },
      ].filter((col) => !(col.key === 'action' && !canEdit)),
    [t, canEdit, props.onInstallationUpdated, deleteInstallation, isSuperAdminOrCompanyAdmin],
  );

  return (
    <Table
      size={'large'}
      columns={columns}
      dataSource={props.data}
      loading={props.loading}
      rowKey={(record) => record.uuid}
      pagination={false}
      onRow={(record) => {
        return {
          onClick: () => {
            props.onRowClick(record);
          },
        };
      }}
      locale={{ emptyText: <EmptyState title={tCommon('emptyTable')} /> }}
    />
  );
};

export default InstallationTable;
